import { useState, useEffect, useCallback } from 'react';
import { pwa } from '../utils/pwa';

export interface PWAState {
  isInstalled: boolean;
  canInstall: boolean;
  isOnline: boolean;
  updateAvailable: boolean;
  installing: boolean;
  notificationPermission: NotificationPermission;
}

export const usePWA = () => {
  const [state, setState] = useState<PWAState>({
    isInstalled: false,
    canInstall: false,
    isOnline: navigator.onLine,
    updateAvailable: false,
    installing: false,
    notificationPermission: 'default'
  });

  // Update state helper
  const updateState = useCallback((updates: Partial<PWAState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Install the app
  const installApp = useCallback(async () => {
    if (!state.canInstall) {
      return false;
    }

    updateState({ installing: true });
    
    try {
      const result = await pwa.showInstallPrompt();
      return result;
    } finally {
      updateState({ installing: false });
    }
  }, [state.canInstall, updateState]);

  // Update service worker
  const updateApp = useCallback(async () => {
    await pwa.updateServiceWorker();
  }, []);

  // Request notification permission
  const requestNotifications = useCallback(async () => {
    const permission = await Notification.requestPermission();
    updateState({ notificationPermission: permission });
    
    if (permission === 'granted') {
      await pwa.setupPushNotifications();
    }
    
    return permission;
  }, [updateState]);

  // Schedule background sync
  const syncOfflineData = useCallback(async () => {
    return await pwa.scheduleBackgroundSync();
  }, []);

  useEffect(() => {
    // Initialize state
    updateState({
      isInstalled: pwa.isAppInstalled(),
      canInstall: pwa.canInstall(),
      notificationPermission: 'Notification' in window ? Notification.permission : 'denied'
    });

    // Listen for PWA events
    const handleInstallAvailable = () => {
      updateState({ canInstall: true });
    };

    const handleInstalled = () => {
      updateState({ 
        isInstalled: true, 
        canInstall: false 
      });
    };

    const handleUpdateAvailable = () => {
      updateState({ updateAvailable: true });
    };

    const handleNetworkChange = (event: CustomEvent) => {
      updateState({ isOnline: event.detail.online });
    };

    // Setup event listeners
    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-installed', handleInstalled);
    window.addEventListener('pwa-update-available', handleUpdateAvailable);
    window.addEventListener('network-status-changed', handleNetworkChange as EventListener);

    // Setup network monitoring
    pwa.setupNetworkMonitoring();

    // Cleanup
    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-installed', handleInstalled);
      window.removeEventListener('pwa-update-available', handleUpdateAvailable);
      window.removeEventListener('network-status-changed', handleNetworkChange as EventListener);
    };
  }, [updateState]);

  return {
    ...state,
    installApp,
    updateApp,
    requestNotifications,
    syncOfflineData
  };
};

// Hook for managing offline/online status
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        // Trigger data sync when coming back online
        pwa.scheduleBackgroundSync();
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    const handleNetworkChange = (event: CustomEvent) => {
      const { online } = event.detail;
      if (online) {
        handleOnline();
      } else {
        handleOffline();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('network-status-changed', handleNetworkChange as EventListener);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('network-status-changed', handleNetworkChange as EventListener);
    };
  }, [wasOffline]);

  return {
    isOnline,
    wasOffline
  };
};

// Hook for managing app installation
export const useAppInstall = () => {
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    setCanInstall(pwa.canInstall());
    setIsInstalled(pwa.isAppInstalled());

    const handleInstallAvailable = () => setCanInstall(true);
    const handleInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-installed', handleInstalled);

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!canInstall) return false;

    setInstalling(true);
    try {
      const result = await pwa.showInstallPrompt();
      return result;
    } finally {
      setInstalling(false);
    }
  }, [canInstall]);

  return {
    canInstall,
    isInstalled,
    installing,
    install
  };
};

// Hook for managing push notifications
export const usePushNotifications = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    const newPermission = await Notification.requestPermission();
    setPermission(newPermission);

    if (newPermission === 'granted') {
      const sub = await pwa.setupPushNotifications();
      setSubscription(sub);
    }

    return newPermission;
  }, []);

  const sendLocalNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (permission === 'granted') {
      return new Notification(title, {
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        ...options
      });
    }
    return null;
  }, [permission]);

  return {
    permission,
    subscription,
    requestPermission,
    sendLocalNotification,
    canNotify: permission === 'granted'
  };
};

// Hook for managing app updates
export const useAppUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const handleUpdateAvailable = () => {
      setUpdateAvailable(true);
    };

    window.addEventListener('pwa-update-available', handleUpdateAvailable);

    return () => {
      window.removeEventListener('pwa-update-available', handleUpdateAvailable);
    };
  }, []);

  const updateApp = useCallback(async () => {
    setUpdating(true);
    try {
      await pwa.updateServiceWorker();
    } finally {
      setUpdating(false);
    }
  }, []);

  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  return {
    updateAvailable,
    updating,
    updateApp,
    dismissUpdate
  };
};